import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, Lock, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { GoogleAuthButton } from '@/components/auth/GoogleAuthButton';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const { login, demoLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(' ')[0]}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDemo = async () => {
    setBusy(true);
    try {
      const u = await demoLogin();
      toast.success(`Demo session for ${u.name.split(' ')[0]}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Demo login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(var(--background))]">
      <div className="relative hidden lg:flex flex-col justify-between p-10 bg-[hsl(var(--surface-2))] overflow-hidden">
        <div className="absolute inset-0 hero-radial-light dark:hero-radial-dark" />
        <Link to="/" className="relative" data-testid="login-logo-link">
          <BrandLogo height={52} testId="login-brand" />
        </Link>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-semibold tracking-tight leading-tight">
            “This app paid for itself in the first month — I caught ₹4,200 of missed rewards.”
          </h2>
          <div className="mt-6 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-accent/15 grid place-items-center text-accent font-semibold">RS</div>
            <div>
              <div className="text-sm font-medium">Rohan Sharma</div>
              <div className="text-xs text-muted-foreground">Product manager, Bengaluru</div>
            </div>
          </div>
        </div>
        <div className="relative text-xs text-muted-foreground">No card numbers stored. We model your reward rules only.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <Link to="/" className="flex items-center" data-testid="login-logo-link-mobile">
              <BrandLogo height={36} testId="login-brand-mobile" />
            </Link>
            <ThemeToggle />
          </div>
          <div className="hidden lg:flex justify-end mb-6">
            <ThemeToggle />
          </div>

          <Card className="p-8">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome back</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">Sign in to optimize your next swipe.</p>

            <Button
              type="button"
              onClick={handleDemo}
              disabled={busy}
              className="w-full mt-6 gap-2 h-11 bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="login-demo-button"
            >
              <Zap className="h-4 w-4" /> Try demo (instant)
            </Button>

            <GoogleAuthButton
              mode="signin"
              disabled={busy}
              className="mt-3"
              testid="login-google-button"
            />

            <div className="my-6 flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">or with email</span>
              <Separator className="flex-1" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" className="pl-10 h-11" data-testid="login-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <a href="#" className="text-xs text-primary hover:underline">Forgot?</a>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" autoComplete="current-password" className="pl-10 h-11" data-testid="login-password-input" />
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 gap-2" data-testid="login-submit-button">
                Sign in <ArrowRight className="h-4 w-4" />
              </Button>
            </form>

            <p className="mt-6 text-sm text-center text-muted-foreground">
              Don’t have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline font-medium">Create one</Link>
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
